<div class="onboarding-checklist flex flex-col gap-5 p-2 sm:p-6 text-primary">
  <div class="flex items-start gap-2">
    <div class="flex-1 min-w-0">
      <h2 class="m-0 text-lg font-semibold">{{ greeting }} 👋</h2>
      <p class="m-0 mt-1 text-sm opacity-75">
        {{ allDone ? 'Your wishlist is below - change it anytime.' : 'Let\'s get you settled. Takes 30 seconds.' }}
      </p>
    </div>
    <button
      mat-icon-button
      type="button"
      (click)="allDone ? requestClose() : remindLater()"
      [attr.aria-label]="allDone ? 'Close' : 'Remind me later'"
      [matTooltip]="allDone ? '' : 'Remind me later'"
      class="!inline-flex"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Payoff once everything's done -->
  <div *ngIf="allDone" class="rounded-xl bg-accent/30 border border-accent p-4 flex flex-col gap-3">
    <p class="m-0 font-semibold">You're set 🎉</p>
    <p class="m-0 text-sm">
      <ng-container *ngIf="savedEntries.length; else nothingWatched">
        We're watching for: <strong>{{ savedLabels }}</strong>.
      </ng-container>
      <ng-template #nothingWatched>Add anything you need below and we'll watch for it.</ng-template>
    </p>
    <div>
      <button mat-flat-button color="primary" type="button" (click)="requestClose()">Start browsing</button>
    </div>
  </div>

  <ol class="m-0 p-0 list-none flex flex-col gap-5">
    <!-- State code and LGA: Google sign-ups don't have them, and the wishlist needs the state -->
    <li *ngIf="showStateCodeStep" class="flex gap-3">
      <span class="step-status" [class.step-status-done]="stateCodeDone" aria-hidden="true">
        <mat-icon *ngIf="stateCodeDone; else stepStateCode">check</mat-icon>
        <ng-template #stepStateCode>1</ng-template>
      </span>
      <div class="flex-1 min-w-0 flex flex-col gap-2">
        <h3 class="m-0 text-base font-medium">Where are you serving?</h3>
        <p *ngIf="stateCodeDone" class="m-0 text-sm opacity-75">Saved. We'll show you what's posted in your state.</p>
        <ng-container *ngIf="!stateCodeDone">
          <p class="m-0 text-sm opacity-75">Your state code and LGA, so we can show you what's posted near you.</p>
          <form class="flex flex-wrap gap-2" (ngSubmit)="hasChanges && !isSaving && onSave()">
            <input
              class="custom-field flex-1 min-w-[10rem]"
              name="stateCode"
              [ngModel]="stateCodeDraft"
              (ngModelChange)="onStateCodeChange($event)"
              [disabled]="isSaving"
              placeholder="State code, e.g. LA/25C/1234"
              aria-label="Your state code"
              [attr.aria-invalid]="stateCodeInvalid"
              aria-describedby="onboarding-state-code-error"
              autocomplete="off"
            />
            <select
              class="custom-field flex-1 min-w-[10rem]"
              name="lga"
              [(ngModel)]="lgaDraft"
              [disabled]="isSaving || !lgaOptions.length"
              aria-label="Your LGA"
            >
              <option value="" disabled>{{ lgaOptions.length ? 'Choose your LGA' : 'Enter your state code first' }}</option>
              <option *ngFor="let lga of lgaOptions" [value]="lga">{{ lga }}</option>
            </select>
          </form>
          <p id="onboarding-state-code-error" *ngIf="stateCodeInvalid" class="m-0 text-sm text-red-700">
            That doesn't look like a state code. It should look like LA/25C/1234.
          </p>
        </ng-container>
      </div>
    </li>

    <!-- Wishlist -->
    <li class="flex gap-3">
      <span class="step-status" [class.step-status-done]="wishlistDone" aria-hidden="true">
        <mat-icon *ngIf="wishlistDone; else stepOne">check</mat-icon>
        <ng-template #stepOne>{{ stepNumber('wishlist') }}</ng-template>
      </span>
      <div class="flex-1 min-w-0 flex flex-col gap-3">
        <div>
          <h3 class="m-0 text-base font-medium">What do you still need?</h3>
          <p class="m-0 text-sm opacity-75">Tap all that apply. When one's posted near you, we'll send you a notification - or an email, if notifications are off.</p>
        </div>

        <div class="flex flex-wrap gap-2" role="group" aria-label="Wishlist suggestions">
          <button
            *ngFor="let suggestion of suggestions"
            type="button"
            class="chip"
            [attr.aria-pressed]="isSelected(suggestion.type, suggestion.value)"
            [disabled]="isLoadingEntries || isSaving"
            (click)="toggleSuggestion(suggestion)"
          >
            <mat-icon *ngIf="isSelected(suggestion.type, suggestion.value)" class="!text-base !w-auto !h-auto">check</mat-icon>
            {{ suggestion.label }}
          </button>

          <button
            *ngFor="let entry of customEntries"
            type="button"
            class="chip chip-selected"
            [disabled]="isSaving"
            (click)="removeFromDraft(entry)"
            [attr.aria-label]="'Remove ' + entry.value + ' from your wishlist'"
          >
            <span class="capitalize">{{ entry.value }}</span>
            <mat-icon class="!text-base !w-auto !h-auto">close</mat-icon>
          </button>

          <button
            *ngIf="!showCustomInput"
            type="button"
            class="chip chip-add"
            [disabled]="isLoadingEntries"
            (click)="showCustomInput = true"
          >
            <mat-icon class="!text-base !w-auto !h-auto">add</mat-icon>
            Something else
          </button>
        </div>

        <form *ngIf="showCustomInput" class="flex flex-wrap items-center gap-2" (ngSubmit)="addCustom()">
          <div class="custom-toggle" role="group" aria-label="Item or place">
            <button type="button" [class.custom-toggle-selected]="customType === 'sale'" [attr.aria-pressed]="customType === 'sale'" (click)="setCustomType('sale')">Item</button>
            <button type="button" [class.custom-toggle-selected]="customType === 'accommodation'" [attr.aria-pressed]="customType === 'accommodation'" (click)="setCustomType('accommodation')">Place</button>
          </div>
          <input
            *ngIf="customType === 'sale'; else placeSelect"
            class="custom-field flex-1 min-w-[10rem]"
            name="customValue"
            [(ngModel)]="customValue"
            placeholder="e.g. reading table"
            aria-label="What item do you need?"
            autocomplete="off"
          />
          <ng-template #placeSelect>
            <!-- Places pick from the postable types, so alerts and counts can match them. -->
            <select class="custom-field flex-1 min-w-[10rem]" name="customValue" [(ngModel)]="customValue" aria-label="What kind of place?">
              <option value="" disabled>Choose a type of place</option>
              <option *ngFor="let type of accommodationTypes" [value]="type">{{ type }}</option>
            </select>
          </ng-template>
          <button mat-stroked-button type="submit" [disabled]="!customValue.trim()">Add</button>
        </form>

        <!-- Instant value: what's already listed nearby -->
        <ul *ngIf="entriesWithMatches.length" class="m-0 p-0 list-none flex flex-col gap-1">
          <li *ngFor="let item of entriesWithMatches" class="flex flex-wrap items-center gap-x-2 text-sm">
            <mat-icon class="!text-base !w-auto !h-auto">local_offer</mat-icon>
            <span>
              <strong class="capitalize">{{ item.entry.value }}</strong>:
              {{ item.matches.count }} listed in {{ item.matches.state | titlecase }} right now
            </span>
            <button type="button" class="font-medium underline" [disabled]="isSaving" (click)="viewMatches(item.entry, item.matches)">View</button>
          </li>
        </ul>
      </div>
    </li>

    <!-- Notifications: asked once there's something to be notified about -->
    <li class="flex gap-3" [class.opacity-60]="!hasWishlistItems && !pushDone">
      <span class="step-status" [class.step-status-done]="pushDone" aria-hidden="true">
        <mat-icon *ngIf="pushDone; else stepTwo">check</mat-icon>
        <ng-template #stepTwo>{{ stepNumber('push') }}</ng-template>
      </span>
      <div class="flex-1 min-w-0 flex flex-col gap-2">
        <h3 class="m-0 text-base font-medium">
          Get alerted when they're posted
          <span class="ml-1 align-middle rounded px-1.5 py-0.5 text-xs font-normal bg-primary/10">Optional</span>
        </h3>
        <ng-container *ngIf="pushDone">
          <p *ngIf="pushOnThisDevice" class="m-0 text-sm opacity-75">On for this device. You can change this anytime from your profile.</p>
          <!-- Answered ("Not now", or on another device) but this device doesn't get them - offer it again. -->
          <ng-container *ngIf="!pushOnThisDevice">
            <p class="m-0 text-sm opacity-75">
              Off on this device, so we'll email you instead.
              <ng-container *ngIf="pushUnavailableHelp">{{ pushUnavailableHelp }}</ng-container>
            </p>
            <div *ngIf="!pushUnavailableHelp">
              <button mat-stroked-button type="button" [disabled]="isTogglingPush" (click)="turnOnPushOnThisDevice()">Turn on alerts</button>
            </div>
          </ng-container>
        </ng-container>
        <p *ngIf="!pushDone && !hasWishlistItems" class="m-0 text-sm opacity-75">Pick something above first.</p>
        <ng-container *ngIf="!pushDone && hasWishlistItems">
          <p class="m-0 text-sm">
            Want a notification the moment someone posts
            <ng-container *ngIf="draft.length; else genericWishlist"><strong>{{ draftLabels }}</strong>?</ng-container>
            <ng-template #genericWishlist>something on your wishlist?</ng-template>
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <button mat-flat-button color="primary" type="button" [disabled]="isTogglingPush" (click)="enablePushNotifications()">
              Turn on alerts
            </button>
            <button mat-button type="button" [disabled]="isTogglingPush" (click)="declinePushNotifications()">Not now</button>
          </div>
        </ng-container>
      </div>
    </li>

    <!-- First name: email sign-ups don't ask for it -->
    <li class="flex gap-3">
      <span class="step-status" [class.step-status-done]="savedFirstName" aria-hidden="true">
        <mat-icon *ngIf="savedFirstName; else stepThree">check</mat-icon>
        <ng-template #stepThree>{{ stepNumber('name') }}</ng-template>
      </span>
      <div class="flex-1 min-w-0 flex flex-col gap-2">
        <h3 class="m-0 text-base font-medium">
          What should we call you?
        </h3>
        <p class="m-0 text-sm opacity-75">So other corps members know who they're chatting with.</p>
        <form class="flex" (ngSubmit)="hasChanges && !isSaving && onSave()">
          <input
            class="custom-field flex-1 min-w-[10rem] max-w-xs"
            name="firstName"
            [(ngModel)]="firstNameDraft"
            [disabled]="isSaving"
            placeholder="Your first name"
            aria-label="Your first name"
            autocomplete="given-name"
            [maxlength]="nameMaxLength"
          />
        </form>
      </div>
    </li>
  </ol>

  <div class="flex flex-wrap items-center justify-end gap-2">
    <button *ngIf="!allDone" mat-button type="button" [disabled]="isSaving" (click)="remindLater()">Remind me later</button>
    <button mat-flat-button color="primary" type="button" [disabled]="isSaving || !hasChanges" (click)="onSave()">
      <ng-container *ngIf="isSaving; else saveLabel">Saving...</ng-container>
      <ng-template #saveLabel>
        <span *ngIf="!hasChanges && hasSavedAnything; else plainSave" class="inline-flex items-center gap-1">
          <mat-icon class="!text-lg !w-auto !h-auto !m-0">check</mat-icon>
          Saved
        </span>
        <ng-template #plainSave>Save</ng-template>
      </ng-template>
    </button>
  </div>
</div>
